import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SessionService } from './session.service';

export interface UploadDocumentoParams {
  matricula: string;
  idOrgao: number | string;
  idOrigem?: number | string;
  idBeneficio?: number | string;
  tipoDocumento?: string;
  idTipoDocumento?: number | string;
}

export interface TipoDocumentoExigido {
  idTipoDocumento: number;
  nome: string;
  tipoDocumento: string;
  categoria: 'identificacao' | 'funcional';
  obrigatorio: boolean;
  ordem: number;
  /** Ids de outros tipos de documento aceitos pelo item (opcional; o servidor pode não enviar). */
  idsEquivalentes?: number[];
  /** Nomes de outros tipos de documento aceitos pelo item (opcional; o servidor pode não enviar). */
  nomesEquivalentes?: string[];
}

export interface DocumentoAnexado {
  id: number;
  idTipoDocumento: number;
  tipoDocumentoNome: string;
  nomeArquivo: string;
  hash: string;
  hashAssinado?: string;
  dataGeracao: string;
  quantidadePaginas?: number;
  assinado: boolean;
}

@Injectable({ providedIn: 'root' })
export class DocumentosService {
  private urlServer = environment.URL_API;

  constructor(private http: HttpClient, private session: SessionService) {}

  uploadDocumento(file: File, paramsInput: UploadDocumentoParams): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.session.token}`,
      // não setamos Content-Type — o browser preenche com o boundary do multipart
    });

    let params = new HttpParams()
      .set('matricula', String(paramsInput.matricula))
      .set('idOrgao', String(paramsInput.idOrgao));

    // mantém os params vazios, como no exemplo do backend
    params = params.set('idOrigem', paramsInput.idOrigem != null ? String(paramsInput.idOrigem) : '');
    params = params.set('idBeneficio', paramsInput.idBeneficio != null ? String(paramsInput.idBeneficio) : '');
    params = params.set('tipoDocumento', paramsInput.tipoDocumento ?? '');
    params = params.set('idTipoDocumento', paramsInput.idTipoDocumento != null ? String(paramsInput.idTipoDocumento) : '');

    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<any>(`${this.urlServer}/files/upload`, formData, {
      headers,
      params,
    });
  }

  listarPorVinculo(idVinculo: number | string): Observable<DocumentoAnexado[]> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.session.token}`,
    });
    const params = new HttpParams().set('idVinculo', String(idVinculo));
    return this.http.get<DocumentoAnexado[]>(`${this.urlServer}/files/lista-por-vinculo`, {
      headers,
      params,
    });
  }

  /**
   * Documentos que a abertura do processo confere (mesma consulta do servidor): ativos e ainda sem
   * benefício da matrícula/órgão, de qualquer origem — vínculo, comprovantes das abas 4 a 6 e o
   * requerimento antigo sem origem. Base das abas 9 e 11.
   */
  listarPreProcesso(matricula: string, idOrgao: number | string): Observable<DocumentoAnexado[]> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.session.token}`,
    });
    const params = new HttpParams().set('matricula', String(matricula)).set('idOrgao', String(idOrgao));
    return this.http.get<DocumentoAnexado[]>(`${this.urlServer}/files/lista-pre-processo`, {
      headers,
      params,
    });
  }

  listarExigidos(tipoBeneficio: string): Observable<TipoDocumentoExigido[]> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.session.token}`,
    });
    const params = new HttpParams().set('tipoBeneficio', tipoBeneficio);
    return this.http.get<TipoDocumentoExigido[]>(`${this.urlServer}/tipo-documento/exigidos`, {
      headers,
      params,
    });
  }

  listarTiposDocumento(): Observable<{ id: number; nome: string; descricao: string }[]> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.session.token}`,
    });
    return this.http.get<{ id: number; nome: string; descricao: string }[]>(
      `${this.urlServer}/tipo-documento`,
      { headers }
    );
  }

  download(hash: string): Observable<Blob> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.session.token}`,
    });
    const params = new HttpParams().set('hash', hash);
    return this.http.get(`${this.urlServer}/files/download`, {
      headers,
      params,
      responseType: 'blob',
    });
  }

  excluir(hash: string): Observable<string> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.session.token}`,
    });
    const params = new HttpParams().set('hash', hash);
    return this.http.delete(`${this.urlServer}/files`, {
      headers,
      params,
      responseType: 'text',
    });
  }
}
